import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MailX, Loader2 } from "lucide-react";
import { usePaymentSettings } from "@/hooks/usePaymentSettings";
import { toast } from "sonner";
import { useState, useEffect } from "react";

const SmsDisableToggleCard = () => {
  const { settings, save } = usePaymentSettings();
  const [localDisabled, setLocalDisabled] = useState(!!settings.smsDisabled);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalDisabled(!!settings.smsDisabled);
  }, [settings.smsDisabled]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await save({ ...settings, smsDisabled: localDisabled });
      toast.success(
        localDisabled ? "SMS sending disabled — emails only." : "SMS sending re-enabled."
      );
    } catch {
      toast.error("Could not update SMS preference.");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = localDisabled !== !!settings.smsDisabled;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MailX className="h-5 w-5" />
          Email-only mode
        </CardTitle>
        <CardDescription>
          Permanently disable all SMS sending. Every payment request, receipt and
          automated reminder will be sent by email instead — which is free and unlimited.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5 pr-4">
            <Label htmlFor="sms-disabled-toggle" className="text-sm font-medium">
              Disable all SMS
            </Label>
            <p className="text-xs text-muted-foreground">
              When on, customers will only ever be contacted by email. Your SMS credits
              will be preserved if you turn it back off later.
            </p>
          </div>
          <Switch
            id="sms-disabled-toggle"
            checked={localDisabled}
            disabled={saving}
            onCheckedChange={setLocalDisabled}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={handleSave} disabled={saving || !hasChanges} size="sm">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SmsDisableToggleCard;
